import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { SEO, pageSEO } from '../components/SEO';
import { NewsletterSignup } from '../components/Newsletter';
import { AdBanner } from '../components/AdSense';
import AfCFTATradeChecker from '../components/AfCFTATradeChecker';
import ExchangeRateTicker from '../components/ExchangeRateTicker';
import { TrustpilotCTASection } from '../components/TrustpilotBadge';
import { 
  HeroSection, 
  StatsSection, 
  CategoriesSection, 
  FeaturesSection, 
  FeaturedProductsSection,
  SupplierStorefrontsSection,
  CTASection, 
  TestimonialsSection,
  AffiliateBanner
} from '../components/home';
import logger from '../lib/logger';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [stats, setStats] = useState({ total_suppliers: 0, total_products: 0, total_buyers: 0 });
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API_URL}/stats`);
        setStats(res.data);
      } catch (err) {
        logger.debug('Stats fetch failed:', err.message);
      }
    };

    const fetchFeaturedProducts = async () => {
      try {
        // Fetch products from correct categories
        const categories = ['agricultural', 'textiles', 'furniture', 'minerals', 'oil_gas', 'vehicles', 'food_beverages'];
        const allProducts = [];
        
        for (const cat of categories) {
          try {
            const res = await axios.get(`${API_URL}/products?category=${cat}&limit=10`);
            if (res.data.products) {
              allProducts.push(...res.data.products);
            }
          } catch (e) {
            // Skip silently - non-critical category fetch
            if (process.env.NODE_ENV === 'development') console.debug('Category fetch skipped:', cat);
          }
        }
        
        // Take up to 70 products
        setFeaturedProducts(allProducts.slice(0, 70));
      } catch (err) {
        logger.debug('Featured products fetch failed:', err.message);
      }
    };

    fetchStats();
    fetchFeaturedProducts();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO {...pageSEO.home} url="https://afriqoma.com/" />
      
      {/* Live Exchange Rate Ticker */}
      <ExchangeRateTicker />
      
      {/* Featured Products - TOP of page */}
      <FeaturedProductsSection products={featuredProducts} />
      
      {/* Hero Section */}
      <HeroSection stats={stats} />
      
      {/* Supplier Storefronts */}
      <SupplierStorefrontsSection />
      
      {/* Affiliate Banner */}
      <AffiliateBanner />
      
      {/* Stats Bar */}
      <StatsSection />
      
      {/* Categories */}
      <CategoriesSection />
      
      {/* AfCFTA Trade Checker Tool */}
      <section className="py-16 bg-gradient-to-b from-charcoal to-gray-900" id="afcfta-checker">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-heading font-bold text-white mb-4">
              AfCFTA Trade Savings Calculator
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Calculate your potential duty savings under the African Continental Free Trade Area agreement. 
              Check compliance requirements and find payment providers instantly.
            </p>
          </div>
          <AfCFTATradeChecker />
        </div>
      </section>
      
      {/* Why Choose Afriqoma */}
      <FeaturesSection />
      
      {/* Testimonials */}
      <TestimonialsSection />
      
      {/* Trustpilot Social Proof */}
      <TrustpilotCTASection />
      
      {/* Newsletter */}
      <section className="py-16 bg-bone">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
          <NewsletterSignup />
        </div>
      </section>
      
      {/* CTA */}
      <CTASection />
      
      {/* AdSense */}
      <AdBanner className="max-w-7xl mx-auto px-4 py-8" />
    </div>
  );
}
